import { FC, ReactElement, useMemo } from "react";
import { Box, Card, styled, Button, Stack } from "@mui/material";

import { InfoTooltip, PlaceholderCard, VendorsTable } from "@/components";
import { tokens } from "@/tokens";
import { columns } from "@/columns/COMMON/vendors";
import { useAppSelector } from "@/store";
import { metrics_placeholder, VENDOS_TOTAL_FIELDS } from "@/constants";
import { roles } from "@/roles";
import { responsiveFontSizes, pxToRem } from "@/utils/responsive";
import { handleCSVDownload } from "@/utils";

export const StyledBox = styled(Box)(() => ({
  flex: 1,
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "collapse",

    "& .MuiTableCell-root": {
      border: 0,
    },

    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: `${pxToRem(7)} ${pxToRem(12)} !important`,
      borderBlock: `1px solid ${tokens.border.light}`,
    },

    "& .MuiTableFooter-root .MuiTableCell-root": {
      background: tokens.bg.hover,
      fontSize: responsiveFontSizes.lg,
      fontWeight: tokens.fontWeight.semibold,
      color: tokens.text.primary,
    },
  },
}));

const VendorsCard: FC<{ data: IVendor[]; referenceRowCount?: number }> = ({ data, referenceRowCount }): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const { customsDuty, customsValue, percentCustomValue } = useMemo(() => {
    const customsDuty = data.reduce(
      (prev, { tariffDuty }) => prev + tariffDuty,
      0
    );
    const customsValue = data.reduce(
      (prev, { tariffValue }) => prev + tariffValue,
      0
    );
    const percentCustomValue = customsValue ? ((customsDuty / customsValue) * 100) : 0;

    return {
      customsDuty,
      customsValue,
      percentCustomValue,
    };
  }, [data]);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxHeight: { xs: 360, md: 400 },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" p={{ xs: 1.5, md: 2 }}>
        <InfoTooltip title="Manufacturers" text="Manufacturers Tooltip" />
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            const headers = [
              "Manufacturer",
              "Manufacturer ID",
              "Customs Value",
              "Customs Duty",
              "Effective Duty Rate (%)",
            ];
            const csvData = data.map((r) => [
              r.manufacturerName ?? r.manufacturer,
              r.manufacturer,
              `$${Number(r.tariffValue ?? 0)}`,
              `$${Number(r.tariffDuty ?? 0)}`,
              (r.tariffValue ?? 0) > 0 ? (((r.tariffDuty ?? 0) / (r.tariffValue ?? 1)) * 100).toFixed(2) : "0.00",
            ]);
            handleCSVDownload(headers, csvData, "manufacturers.csv");
          }}
        >
          Download CSV
        </Button>
      </Stack>

      {canSelectClients && !selectedClient ? (
        <PlaceholderCard
          description="Analyze your supplier network's import activity by value, duty exposure, and entry volume to understand key vendor relationships and cost drivers."
          imgUrl={metrics_placeholder}
          centerContent
          isBordered
          cardStyles={{
            m: { xs: 2, md: 2.5 },
            mt: 0,
          }}
        />
      ) : (
        <VendorsTable
          columns={columns}
          contentIdKey="manufacturer"
          data={data}
          footerColumns={VENDOS_TOTAL_FIELDS}
          footerData={{
            customsDuty,
            customsValue,
            percentCustomValue,
          }}
          isLoading={false}
          limit={10}
          page={1}
          total={10}
          fillerRowCount={
            referenceRowCount && referenceRowCount > data.length
              ? referenceRowCount - data.length
              : 0
          }
        />
      )}
    </Card>
  );
};

export default VendorsCard;
